(() => {
  const createFormLocalizer = (root, embed) => {
    const source = root.querySelector('[data-bf-klaviyo-copy]');
    if (!source) return () => {};
    let dictionary;
    try { dictionary = JSON.parse(source.textContent); }
    catch { return () => {}; }
    if (!Array.isArray(dictionary.translations)) return () => {};
    const normalize = (value) => String(value || '').normalize('NFKC').replace(/[’‘]/g, "'").replace(/…/g, '...').replace(/\s+/g, ' ').trim().toLowerCase();
    const phrases = new Map();
    dictionary.translations.forEach((entry) => entry.sources.forEach((text) => phrases.set(normalize(text), entry)));
    const lookup = (text) => {
      const direct = phrases.get(normalize(text));
      if (direct) return { ...direct, suffix: '' };
      const suffix = String(text).match(/\s*([*＊:])\s*$/);
      const entry = suffix && phrases.get(normalize(String(text).slice(0, suffix.index)));
      return entry ? { ...entry, suffix: suffix[1] === ':' ? ':' : ' *' } : null;
    };
    const textNodes = (element) => {
      const nodes = [];
      const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (node.nodeValue.trim() && !node.parentElement?.closest('script, style, noscript')) nodes.push(node);
      }
      return nodes;
    };
    const setText = (element, text) => {
      if (normalize(element.textContent) === normalize(text)) return;
      const nodes = textNodes(element);
      nodes.forEach((node, index) => {
        const next = index === 0 ? text : '';
        if (node.nodeValue !== next) node.nodeValue = next;
      });
    };
    const setAttribute = (element, name, text) => {
      if (element.getAttribute(name) !== text) element.setAttribute(name, text);
    };
    const localizePrivacy = (element, entry) => {
      const link = element.querySelector('a');
      const parts = entry.kind === 'privacy_read' ? dictionary.privacyRead : dictionary.privacyAgree;
      if (!link || !parts || element.querySelectorAll('a').length !== 1) return false;
      const nodes = textNodes(element);
      const before = nodes.filter((node) => !link.contains(node) && Boolean(node.compareDocumentPosition(link) & Node.DOCUMENT_POSITION_FOLLOWING));
      const after = nodes.filter((node) => !link.contains(node) && Boolean(node.compareDocumentPosition(link) & Node.DOCUMENT_POSITION_PRECEDING));
      before.forEach((node, index) => {
        const text = index === 0 ? parts.before : '';
        if (node.nodeValue !== text) node.nodeValue = text;
      });
      setText(link, parts.link);
      after.forEach((node, index) => {
        const text = index === 0 ? parts.after : '';
        if (node.nodeValue !== text) node.nodeValue = text;
      });
      return true;
    };
    return () => {
      embed.querySelectorAll('h1, h2, h3, h4, h5, h6, p, label, [role="alert"]').forEach((element) => {
        const entry = lookup(element.textContent);
        if (!entry) return;
        if (element.querySelector('a')) {
          if (entry.kind === 'privacy_read' || entry.kind === 'privacy_agree') localizePrivacy(element, entry);
          return;
        }
        setText(element, entry.text + entry.suffix);
      });
      textNodes(embed).forEach((node) => {
        if (node.parentElement?.closest('button')) return;
        const entry = lookup(node.nodeValue);
        if (!entry) return;
        const leading = node.nodeValue.match(/^\s*/)[0];
        const trailing = node.nodeValue.match(/\s*$/)[0];
        const text = leading + entry.text + entry.suffix + trailing;
        if (node.nodeValue !== text) node.nodeValue = text;
      });
      embed.querySelectorAll('[placeholder], [aria-label], [title]').forEach((element) => {
        ['placeholder', 'aria-label', 'title'].forEach((attribute) => {
          const original = element.getAttribute(attribute);
          if (original === null) return;
          const entry = lookup(original);
          if (entry) setAttribute(element, attribute, entry.text + entry.suffix);
        });
      });
      embed.querySelectorAll('button:not([aria-haspopup]):not([role="combobox"]):not([type="reset"])').forEach((button) => {
        const caption = normalize(button.textContent);
        if (!['>', '→', '↗', 'submit', 'absenden', 'send', 'invia', 'envoyer', 'enviar'].includes(caption) && caption !== normalize(dictionary.submitText)) return;
        setText(button, dictionary.submitText);
        setAttribute(button, 'aria-label', dictionary.submitText);
      });
    };
  };

  const initialize = (root) => {
    if (root.dataset.bfInitialized === 'true') return;
    root.dataset.bfInitialized = 'true';

    const controller = new AbortController();
    const { signal } = controller;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const hero = root.querySelector('.jf-bf2026__hero');
    const intro = root.querySelector('.jf-bf2026__intro');
    const toggle = root.querySelector('[data-bf-motion-toggle]');
    const sections = [...root.querySelectorAll('[data-bf-step]')];
    const chapterLinks = [...root.querySelectorAll('[data-bf-step-link]')];
    let motionPaused = false;
    let frame = 0;

    const updateScroll = () => {
      frame = 0;
      if (!root.isConnected) return;
      const viewport = window.innerHeight;
      let active = 0;
      sections.forEach((section, index) => {
        if (section.getBoundingClientRect().top < viewport * .48) active = index;
      });
      chapterLinks.forEach((link, index) => {
        if (index === active) link.setAttribute('aria-current', 'step');
        else link.removeAttribute('aria-current');
      });
      const heroRect = hero.getBoundingClientRect();
      root.dataset.heroVisible = String(heroRect.bottom > 0 && heroRect.top < viewport);
      const mayMove = root.dataset.scrollMotion === 'true' && !reducedMotion.matches && !motionPaused;
      if (mayMove && window.innerWidth > 740) {
        root.style.setProperty('--bf-hero-shift', `${Math.min(22, Math.max(-22, -heroRect.top * .035))}px`);
        const imageShift = (viewport * .5 - intro.getBoundingClientRect().top) * .035;
        root.style.setProperty('--bf-image-shift', `${Math.min(20, Math.max(-20, imageShift))}px`);
      } else {
        root.style.removeProperty('--bf-hero-shift');
        root.style.removeProperty('--bf-image-shift');
      }
    };
    const requestScroll = () => { if (!frame) frame = window.requestAnimationFrame(updateScroll); };
    window.addEventListener('scroll', requestScroll, { passive: true, signal });
    window.addEventListener('resize', requestScroll, { passive: true, signal });

    const applyMotion = () => {
      root.dataset.motionPaused = String(motionPaused || reducedMotion.matches);
      toggle.hidden = root.dataset.pixelMotion !== 'true' || reducedMotion.matches;
      toggle.setAttribute('aria-pressed', String(motionPaused));
      toggle.setAttribute('aria-label', motionPaused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel);
      requestScroll();
    };
    toggle.addEventListener('click', () => { motionPaused = !motionPaused; applyMotion(); }, { signal });
    reducedMotion.addEventListener('change', applyMotion, { signal });
    document.addEventListener('visibilitychange', () => {
      root.dataset.heroVisible = String(!document.hidden && hero.getBoundingClientRect().bottom > 0);
    }, { signal });
    applyMotion();

    let observer;
    if ('IntersectionObserver' in window && root.dataset.scrollMotion === 'true' && !reducedMotion.matches) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { threshold: .08, rootMargin: '0px 0px -36px 0px' });
      root.querySelectorAll('[data-bf-reveal]').forEach((element) => observer.observe(element));
      root.dataset.revealReady = 'true';
    }

    const wrapper = root.querySelector('[data-bf-klaviyo]');
    const embed = root.querySelector('[data-bf-klaviyo-embed]');
    let formObserver;
    let delayedTimer;
    if (wrapper && embed) {
      const localizeForm = createFormLocalizer(root, embed);
      const status = root.querySelector('[data-bf-klaviyo-status]');
      const skeleton = root.querySelector('[data-bf-klaviyo-skeleton]');
      const loading = root.querySelector('[data-bf-klaviyo-loading]');
      const delayed = root.querySelector('[data-bf-klaviyo-delayed]');
      const showForm = () => {
        if (!embed.querySelector('form, .klaviyo-form, [data-testid="form-component"], input, button')) return;
        wrapper.setAttribute('aria-busy', 'false');
        status.hidden = true;
        window.clearTimeout(delayedTimer);
        try {
          localizeForm();
          wrapper.dataset.formTextState = 'localized';
        } catch {
          wrapper.dataset.formTextState = 'original';
        }
        requestScroll();
      };
      if ('MutationObserver' in window) {
        formObserver = new MutationObserver(showForm);
        formObserver.observe(embed, { childList: true, characterData: true, attributes: true, attributeFilter: ['placeholder', 'aria-label', 'title'], subtree: true });
      }
      delayedTimer = window.setTimeout(() => {
        if (status.hidden) return;
        skeleton.hidden = true;
        loading.hidden = true;
        delayed.hidden = false;
        wrapper.setAttribute('aria-busy', 'false');
      }, 18000);
      root.querySelector('[data-bf-klaviyo-retry]').addEventListener('click', () => window.location.reload(), { signal });
      window.addEventListener('klaviyoForms', (event) => {
        if (event.detail?.formId !== root.dataset.klaviyoFormId) return;
        if (event.detail.type === 'embedOpen') showForm();
        if (event.detail.type === 'submit' || event.detail.type === 'stepSubmit') {
          wrapper.closest('[data-bf-reveal]')?.classList.add('is-visible');
        }
      }, { signal });
      showForm();
    }
    updateScroll();
    root.bfCleanup = () => {
      controller.abort();
      observer?.disconnect();
      window.cancelAnimationFrame(frame);
      formObserver?.disconnect();
      window.clearTimeout(delayedTimer);
    };
  };

  const initializeAll = (scope) => scope.querySelectorAll('[data-jf-bf2026]').forEach(initialize);
  initializeAll(document);
  if (!window.jfBF2026EditorEvents) {
    document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      event.target.querySelectorAll('[data-jf-bf2026]').forEach((root) => root.bfCleanup?.());
    });
    window.jfBF2026EditorEvents = true;
  }
})();
